import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getFlowByID } from "@redotech/redo-merchant-app-common/client/team";
import { DraftReturn } from "@redotech/redo-model/draft-return/draft-return";
import { Return, ReturnTypeEnum } from "@redotech/redo-model/return";
import {
  FlowType,
  returnFlowJsonFormat,
} from "@redotech/redo-model/return-flow";
import {
  BlockConfig,
  EdgeConfig,
  Selection,
  SelectionMode,
  SelectionTheme,
} from "@redotech/redo-web/flowchart";
import { assertNever } from "@redotech/util/type";
import { useMemo } from "react";
import { modelToSteps } from "../../setting/flow-editor/flow-editor";
import * as returnFlowCss from "../../setting/return-flow/return-flow.module.css";
import { Step } from "../../setting/return-flow/step";
import { getStepTitle } from "./use-reset-modal";

export enum IconType {
  CHECK_CIRCLE = "check-circle",
  REFRESH_CCW = "refresh-ccw",
}

interface UseReturnPathModalResponse {
  flowchartProps: {
    blocks: BlockConfig[];
    edges: EdgeConfig[];
    selectionMode: SelectionMode;
    onSelect: (s: Selection) => void;
    selected: Selection;
  };
}

export const useReturnPathModal = ({
  return_,
  draftReturn,
  returnPathItemId,
}: {
  return_: Return;
  draftReturn: DraftReturn;
  returnPathItemId?: string;
}): UseReturnPathModalResponse => {
  const client = useRequiredContext(RedoMerchantClientContext);
  const path = draftReturn.pendingReturnItems.find(
    (item) =>
      item.returnableItemId === returnPathItemId ||
      item.returnableItemId.split("-")[0] === returnPathItemId,
  )?.flowPath;

  const flowLoad = useLoad(
    async (signal) => {
      let flowType: FlowType;
      const type = return_.type as ReturnTypeEnum;
      switch (type) {
        case ReturnTypeEnum.CLAIM:
        case ReturnTypeEnum.MANAGED_CLAIM:
          flowType = FlowType.CLAIM;
          break;
        case ReturnTypeEnum.WARRANTY:
          flowType = FlowType.WARRANTY;
          break;
        case ReturnTypeEnum.RETURN:
          flowType = FlowType.RETURN;
          break;
        default:
          assertNever(type);
      }
      const { flow } = await getFlowByID(
        client,
        flowType,
        draftReturn.flow._id,
        { signal },
      );
      const returnFlow = returnFlowJsonFormat.read(flow);
      const flowSteps = modelToSteps(returnFlow, flowType);
      return flowSteps;
    },
    [return_.type],
  );

  const steps: Map<string, Step> = useMemo(() => {
    if (!flowLoad.value) {
      return new Map();
    }
    return flowLoad.value;
  }, [flowLoad.value]);

  const selectionMode: SelectionMode = useMemo(
    () => ({
      enabled: (id) => !!path?.includes(Number(id)),
      theme: SelectionTheme.SELECTED,
      edgeHighlight: (to, from) => {
        const fromIndex = path?.indexOf(Number(from));
        const toIndex = path?.indexOf(Number(to));

        return (
          fromIndex !== undefined &&
          toIndex !== undefined &&
          fromIndex + 1 === toIndex
        );
      },
    }),
    [path],
  );

  const blocks: BlockConfig[] = [...steps.entries()].map(([id, step]) => ({
    error: !step.valid(FlowType.RETURN),
    layout: step.layout(),
    title: getStepTitle(step, return_.type as ReturnTypeEnum),
    description: step.description(),
    icon: <step.Icon className={returnFlowCss.icon} />,
    id,
  }));

  const edges: EdgeConfig[] = [...steps.entries()].flatMap(([id, step]) =>
    step
      .downstream()
      .map((downstream) => ({
        id: `${id}.${downstream.id}`,
        from: id,
        to: downstream.id,
        label: downstream.label,
      })),
  );

  return {
    flowchartProps: {
      blocks,
      edges,
      selectionMode,
      onSelect: (s) => {},
      selected: undefined,
    },
  };
};
